import * as React from "react"

import { MOTION_CLASSES } from "@/lib/motion"
import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full rounded-2xl border border-border/60 bg-card/95 text-card-foreground shadow-[var(--shadow-card-elevated)]",
      "transition-shadow",
      MOTION_CLASSES.micro,
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-4 md:p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-[length:var(--fluid-heading-lg)] font-semibold leading-tight tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  // If className contains p-0, remove default padding completely
  const hasP0 = className?.includes('p-0') || className?.includes('!p-0');
  const defaultPadding = hasP0 ? '' : 'p-4 pt-0 md:p-6';

  return (
    <div ref={ref} className={cn(defaultPadding, className)} {...props} />
  );
})
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-4 pt-0 md:p-6", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

/**
 * CardInteractiveItem - Full-width interactive list item for Card with CardContent p-0
 * 
 * Ensures hover background extends to full width of Card container.
 * Uses wrapper approach: outer div for hover (full-width), inner div for padding.
 * 
 * Usage:
 * <CardContent className="p-0">
 *   <CardInteractiveItem onClick={...} aria-label="...">
 *     <div className="flex items-center justify-between p-4 md:p-6">
 *       content here
 *     </div>
 *   </CardInteractiveItem>
 * </CardContent>
 */
interface CardInteractiveItemProps extends React.HTMLAttributes<HTMLDivElement> {
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  'aria-label'?: string;
}

const CardInteractiveItem = React.forwardRef<
  HTMLDivElement,
  CardInteractiveItemProps
>(({ className, children, onClick, onKeyDown, 'aria-label': ariaLabel, ...props }, ref) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
    onKeyDown?.(e);
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      className={cn(
        "block w-full cursor-pointer",
        "hover:bg-accent/50 hover:text-accent-foreground",
        "transition-all duration-240 ease-standard",
        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 focus-visible:outline-none",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
CardInteractiveItem.displayName = "CardInteractiveItem"

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardInteractiveItem, CardTitle }

